import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { StreamFrame, StreamRecord, StreamTrackerService } from '../stream-tracker.service';

type DetailPane = 'live' | 'frames' | 'request';

/**
 * Onglet « Stream » : suit en direct les flux `fetch`/SSE et `EventSource`.
 *
 * Trois partis pris de mise en page, tirés de l'usage réel :
 *
 * - la liste des flux est une bande horizontale, pas une colonne : dans un
 *   panneau de 720 px, une colonne de liste ampute la seule chose qu'on vient
 *   lire, le contenu du flux ;
 * - la vue par défaut est « Direct » — la question, puis le texte qui s'écrit,
 *   puis les events nommés — parce qu'on ouvre cet onglet pour suivre une
 *   conversation, pas pour éplucher un tableau de trames ;
 * - l'état vide est diagnostique : il dit lequel des cas il vit plutôt que de
 *   laisser croire à une panne.
 */
@Component({
  selector: 'app-dev-tools-stream-tab',
  standalone: true,
  imports: [CommonModule, FormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [
    `
      :host {
        display: block;
        height: 100%;
        color: #f5f5f5;
      }
      .chip {
        display: inline-flex;
        align-items: center;
        gap: 3px;
        padding: 1px 6px;
        border-radius: 4px;
        font-size: 10px;
        font-weight: 600;
        white-space: nowrap;
      }
      .mono,
      pre {
        font-family: 'JetBrains Mono', ui-monospace, monospace;
        white-space: pre-wrap;
        word-break: break-word;
      }
      pre {
        font-size: 11px;
        line-height: 1.45;
        margin: 0;
      }
      .scroll-y {
        overflow-y: auto;
        scrollbar-width: thin;
      }
      .scroll-x {
        overflow-x: auto;
        scrollbar-width: thin;
      }
      .scroll-y::-webkit-scrollbar,
      .scroll-x::-webkit-scrollbar {
        width: 8px;
        height: 8px;
      }
      .scroll-y::-webkit-scrollbar-thumb,
      .scroll-x::-webkit-scrollbar-thumb {
        background: rgba(100, 116, 139, 0.5);
        border-radius: 4px;
      }
      /* Bande des flux : cartes compactes, défilement horizontal. */
      .card {
        flex: 0 0 auto;
        min-width: 150px;
        max-width: 210px;
        border: 1px solid rgba(71, 85, 105, 0.6);
        border-radius: 5px;
        padding: 4px 7px;
        cursor: pointer;
        background: rgba(30, 41, 59, 0.5);
      }
      .card:hover {
        border-color: rgba(148, 163, 184, 0.8);
      }
      .card--active {
        border-color: #3b82f6;
        background: rgba(59, 130, 246, 0.16);
      }
      .dot {
        width: 6px;
        height: 6px;
        border-radius: 50%;
        flex: 0 0 auto;
      }
      .dot--live {
        background: #22c55e;
        animation: pulse 1.1s ease-in-out infinite;
      }
      @keyframes pulse {
        0%,
        100% {
          opacity: 1;
        }
        50% {
          opacity: 0.2;
        }
      }
      /* Le texte du bot : c'est ce qu'on vient lire, il a droit à sa taille. */
      .stream-text {
        font-family: 'JetBrains Mono', ui-monospace, monospace;
        font-size: 12.5px;
        line-height: 1.6;
        white-space: pre-wrap;
        word-break: break-word;
        color: #e2e8f0;
      }
      .caret {
        display: inline-block;
        width: 7px;
        height: 14px;
        background: #22c55e;
        vertical-align: text-bottom;
        animation: blink 1s step-end infinite;
      }
      @keyframes blink {
        50% {
          opacity: 0;
        }
      }
      .evt-row {
        border-left: 2px solid transparent;
      }
      .evt-row:hover {
        background: rgba(148, 163, 184, 0.08);
      }
      table.frames td {
        vertical-align: top;
        padding: 2px 6px;
      }
    `,
  ],
  template: `
    <div class="tw-flex tw-flex-col tw-h-full tw-text-xs tw-text-gray-200">
      <!-- ── Barre d'outils ───────────────────────────────────────────── -->
      <div
        class="tw-flex tw-items-center tw-gap-2 tw-px-2 tw-py-1.5 tw-border-b tw-border-gray-700/60 tw-bg-gray-900/40 tw-flex-shrink-0"
      >
        <span class="chip tw-bg-info-500/20 tw-text-info-500">
          <i class="ph ph-waveform"></i> {{ streams().length }}
        </span>
        <span *ngIf="counts().live > 0" class="chip tw-bg-success-500/20 tw-text-success-500">
          <span class="dot dot--live"></span> {{ counts().live }} en cours
        </span>

        <button
          type="button"
          (click)="follow.set(!follow())"
          class="tw-px-2 tw-py-0.5 tw-rounded tw-text-xs tw-font-medium"
          [ngClass]="follow() ? 'tw-bg-success-500 tw-text-gray-900' : 'tw-bg-gray-700 tw-text-gray-300'"
          title="Afficher automatiquement le flux le plus récent"
        >
          <i class="ph ph-crosshair"></i> Suivre
        </button>

        <input
          type="text"
          [ngModel]="search()"
          (ngModelChange)="search.set($event)"
          placeholder="Filtrer…"
          class="tw-bg-gray-800 tw-border tw-border-gray-700 tw-rounded tw-px-2 tw-py-0.5 tw-text-xs tw-text-gray-200 tw-w-28 placeholder:tw-text-gray-500"
        />

        <div class="tw-ml-auto tw-flex tw-items-center tw-gap-1.5">
          <button
            type="button"
            (click)="tracker.toggleCapture()"
            class="tw-px-2 tw-py-0.5 tw-rounded tw-text-xs tw-font-medium"
            [ngClass]="
              tracker.capturing() ? 'tw-bg-gray-700 tw-text-gray-300' : 'tw-bg-warning-500 tw-text-gray-900'
            "
          >
            <i class="ph" [ngClass]="tracker.capturing() ? 'ph-pause' : 'ph-play'"></i>
          </button>
          <button
            type="button"
            (click)="copyText()"
            [disabled]="!selected()"
            class="tw-px-2 tw-py-0.5 tw-rounded tw-bg-gray-700 hover:tw-bg-gray-600 disabled:tw-opacity-40 tw-text-gray-300"
            [title]="copied() ? 'Copié' : 'Copier le texte du flux'"
          >
            <i class="ph" [ngClass]="copied() ? 'ph-check' : 'ph-copy'"></i>
          </button>
          <button
            type="button"
            (click)="exportNdjson()"
            [disabled]="!selected()"
            class="tw-px-2 tw-py-0.5 tw-rounded tw-bg-gray-700 hover:tw-bg-gray-600 disabled:tw-opacity-40 tw-text-gray-300"
            title="Exporter les trames en NDJSON"
          >
            <i class="ph ph-download-simple"></i>
          </button>
          <button
            type="button"
            (click)="tracker.clear()"
            class="tw-px-2 tw-py-0.5 tw-rounded tw-bg-error-500/80 hover:tw-bg-error-500 tw-text-white"
            title="Vider"
          >
            <i class="ph ph-trash"></i>
          </button>
        </div>
      </div>

      <!-- ── État vide diagnostique ───────────────────────────────────── -->
      <div *ngIf="streams().length === 0" class="tw-flex-1 scroll-y tw-p-4">
        <div class="tw-max-w-md tw-mx-auto">
          <div class="tw-text-center tw-text-gray-400 tw-mb-4">
            <i class="ph ph-waveform tw-text-3xl tw-block tw-mb-1"></i>
            <span class="tw-text-sm">Aucun flux capturé</span>
          </div>

          <div class="tw-space-y-1.5 tw-mb-4">
            <div class="tw-flex tw-items-center tw-gap-2">
              <i
                class="ph"
                [ngClass]="
                  tracker.isInstalled ? 'ph-check-circle tw-text-success-500' : 'ph-x-circle tw-text-error-500'
                "
              ></i>
              <span>Traceur {{ tracker.isInstalled ? 'installé' : 'NON installé' }}</span>
            </div>
            <div class="tw-flex tw-items-center tw-gap-2">
              <i
                class="ph"
                [ngClass]="
                  tracker.seenRequests > 0 ? 'ph-check-circle tw-text-success-500' : 'ph-warning tw-text-warning-500'
                "
              ></i>
              <span>{{ tracker.seenRequests }} requête(s) vue(s) depuis le chargement</span>
            </div>
            <div class="tw-flex tw-items-center tw-gap-2">
              <i class="ph ph-eye tw-text-info-500"></i>
              <span>Surveillance : <code class="tw-text-teal-primary">{{ tracker.watched.join(', ') }}</code></span>
            </div>
          </div>

          <div class="tw-bg-gray-800/60 tw-rounded tw-p-3 tw-text-gray-400 tw-leading-relaxed">
            <div *ngIf="!tracker.isInstalled" class="tw-text-error-500 tw-mb-2">
              Le patch n'est pas posé. Vérifiez <code>ENABLE_DEV_TOOLS</code> puis rechargez la page.
            </div>
            <div *ngIf="tracker.isInstalled && tracker.seenRequests === 0" class="tw-text-warning-500 tw-mb-2">
              Aucune requête n'a traversé le traceur. Rechargez la page complètement
              (le rechargement à chaud ne repose pas le patch).
            </div>
            <div *ngIf="tracker.isInstalled && tracker.seenRequests > 0">
              Des requêtes passent, mais aucune ne correspond. Ouvrez le chat et posez une
              question — ou élargissez la surveillance depuis la console :
              <pre class="tw-mt-1 tw-text-gray-300">__DEV_TOOLS__.stream.watchAll()</pre>
            </div>
          </div>
        </div>
      </div>

      <ng-container *ngIf="streams().length > 0">
        <!-- ── Bande des flux ─────────────────────────────────────────── -->
        <div
          class="tw-flex tw-gap-1.5 tw-px-2 tw-py-1.5 tw-border-b tw-border-gray-700/60 scroll-x tw-flex-shrink-0"
        >
          <div
            *ngFor="let s of streams(); trackBy: trackStream"
            (click)="select(s.id)"
            class="card"
            [class.card--active]="selected()?.id === s.id"
          >
            <div class="tw-flex tw-items-center tw-gap-1.5">
              <span class="dot" [ngClass]="s.state === 'streaming' ? 'dot--live' : ''" [style.background]="dotColor(s)"></span>
              <span class="chip" [ngClass]="transportClass(s.transport)">
                {{ s.transport === 'eventsource' ? 'SSE' : s.method }}
              </span>
              <span class="tw-text-[10px] tw-text-gray-500 tw-ml-auto">{{ duration(s) }}</span>
            </div>
            <div class="tw-truncate tw-text-gray-200 tw-mt-0.5" [title]="s.url">
              {{ s.label || s.shortUrl }}
            </div>
            <div class="tw-text-[10px] tw-text-gray-500">
              {{ tokenCount(s) }} tokens · {{ formatBytes(s.totalBytes) }}
            </div>
          </div>
        </div>

        <!-- ── Sous-onglets ───────────────────────────────────────────── -->
        <div
          class="tw-flex tw-items-center tw-gap-1 tw-px-2 tw-py-1 tw-border-b tw-border-gray-700/60 tw-flex-shrink-0"
        >
          <button
            type="button"
            *ngFor="let p of panes"
            (click)="pane.set(p.id)"
            class="tw-px-2 tw-py-0.5 tw-rounded tw-text-xs tw-font-medium"
            [ngClass]="pane() === p.id ? 'tw-bg-gray-700 tw-text-white' : 'tw-text-gray-400 hover:tw-text-gray-200'"
          >
            <i class="ph" [ngClass]="p.icon"></i> {{ p.label }}
          </button>
          <span *ngIf="selected() as s" class="tw-ml-auto tw-flex tw-items-center tw-gap-2">
            <span class="chip" [ngClass]="stateClass(s.state)">{{ stateLabel(s.state) }}</span>
            <span *ngIf="s.status" class="tw-text-[10px] tw-text-gray-500">HTTP {{ s.status }}</span>
          </span>
        </div>

        <!-- ── Contenu ────────────────────────────────────────────────── -->
        <div class="tw-flex-1 tw-min-h-0" *ngIf="selected() as s">
          <!-- Direct -->
          <div *ngIf="pane() === 'live'" class="tw-h-full tw-flex tw-flex-col">
            <!-- Ce qui a été demandé -->
            <div
              *ngIf="s.label || s.tags"
              class="tw-px-3 tw-py-2 tw-border-b tw-border-gray-800 tw-bg-gray-900/30 tw-flex-shrink-0"
            >
              <div *ngIf="s.label" class="tw-text-gray-100 tw-mb-1">
                <i class="ph ph-arrow-up-right tw-text-info-500"></i> {{ s.label }}
              </div>
              <div class="tw-flex tw-flex-wrap tw-gap-1">
                <span *ngFor="let t of tagList(s)" class="chip tw-bg-gray-700/60 tw-text-gray-300">
                  {{ t.k }}: {{ t.v }}
                </span>
              </div>
            </div>

            <!-- Avertissement : le corps n'est pas du SSE -->
            <div
              *ngIf="rawChunkCount() > 0"
              class="tw-px-3 tw-py-1.5 tw-bg-warning-500/15 tw-text-warning-500 tw-flex-shrink-0"
            >
              <i class="ph ph-warning"></i>
              {{ rawChunkCount() }} ligne(s) hors protocole SSE — le backend n'envoie pas
              <code>event:</code>/<code>data:</code>. Voir l'onglet Trames.
            </div>

            <!-- Le texte qui s'écrit -->
            <div #textBox class="tw-flex-1 scroll-y tw-px-3 tw-py-2 tw-min-h-0">
              <div class="stream-text">{{ s.text }}<span *ngIf="s.state === 'streaming'" class="caret"></span></div>
              <div *ngIf="!s.text && s.state !== 'streaming'" class="tw-text-gray-500 tw-italic">
                Aucun texte sur ce flux — regardez les events ci-dessous.
              </div>
            </div>

            <!-- Events nommés -->
            <div
              class="tw-flex-shrink-0 tw-border-t tw-border-gray-700/60 scroll-y"
              style="max-height: 40%"
              *ngIf="namedEvents().length > 0"
            >
              <div
                *ngFor="let f of namedEvents(); trackBy: trackFrame"
                class="evt-row tw-px-3 tw-py-1 tw-cursor-pointer tw-border-b tw-border-gray-800/50"
                (click)="toggleExpand(f.seq)"
              >
                <div class="tw-flex tw-items-center tw-gap-2">
                  <i
                    class="ph tw-text-gray-500"
                    [ngClass]="isExpanded(f.seq) ? 'ph-caret-down' : 'ph-caret-right'"
                  ></i>
                  <span class="chip" [ngClass]="frameClass(f)">{{ frameLabel(f) }}</span>
                  <span class="tw-text-gray-400 tw-truncate tw-flex-1 mono">{{ summary(f) }}</span>
                  <span class="tw-text-[10px] tw-text-gray-600">+{{ formatMs(f.dt) }}</span>
                </div>
                <pre *ngIf="isExpanded(f.seq)" class="tw-mt-1 tw-ml-5 tw-text-gray-300">{{ detail(f) }}</pre>
              </div>
            </div>
          </div>

          <!-- Trames -->
          <div *ngIf="pane() === 'frames'" #framesBox class="tw-h-full scroll-y">
            <table class="frames tw-w-full tw-table-fixed">
              <tbody>
                <tr *ngFor="let f of frames(); trackBy: trackFrame" class="evt-row tw-border-b tw-border-gray-800/50">
                  <td class="tw-w-16 tw-text-[10px] tw-text-gray-600 mono">+{{ formatMs(f.dt) }}</td>
                  <td class="tw-w-24">
                    <span class="chip" [ngClass]="frameClass(f)">{{ frameLabel(f) }}</span>
                  </td>
                  <td class="mono tw-text-gray-300 tw-text-[11px]">{{ framePreview(f) }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Requête -->
          <div *ngIf="pane() === 'request'" class="tw-h-full scroll-y tw-p-3">
            <div class="tw-text-[10px] tw-text-gray-500 tw-mb-1">URL</div>
            <pre class="tw-text-gray-300 tw-mb-3">{{ s.url }}</pre>

            <ng-container *ngIf="s.tags">
              <div class="tw-text-[10px] tw-text-gray-500 tw-mb-1">Contexte métier</div>
              <pre class="tw-text-teal-primary tw-mb-3">{{ serialize(s.tags) }}</pre>
            </ng-container>

            <ng-container *ngIf="s.requestBody !== undefined">
              <div class="tw-text-[10px] tw-text-gray-500 tw-mb-1">Corps envoyé</div>
              <pre class="tw-text-gray-300 tw-mb-3">{{ serialize(s.requestBody) }}</pre>
            </ng-container>

            <ng-container *ngIf="s.requestHeaders">
              <div class="tw-text-[10px] tw-text-gray-500 tw-mb-1">En-têtes</div>
              <pre class="tw-text-gray-400 tw-mb-3">{{ serialize(s.requestHeaders) }}</pre>
            </ng-container>

            <div *ngIf="s.error">
              <div class="tw-text-[10px] tw-text-error-500 tw-mb-1">Erreur</div>
              <pre class="tw-text-error-500">{{ s.error }}</pre>
            </div>
          </div>
        </div>
      </ng-container>
    </div>
  `,
})
export class DevToolsStreamTabComponent implements OnInit {
  readonly tracker = inject(StreamTrackerService);

  @ViewChild('textBox') textBox?: ElementRef<HTMLDivElement>;
  @ViewChild('framesBox') framesBox?: ElementRef<HTMLDivElement>;

  readonly pinnedId = signal<string | null>(null);
  /** Mode suivi : le flux le plus récent est affiché sans avoir à cliquer. */
  readonly follow = signal(true);
  readonly pane = signal<DetailPane>('live');
  readonly search = signal('');
  readonly copied = signal(false);
  private readonly expanded = signal<ReadonlySet<number>>(new Set());

  readonly panes: { id: DetailPane; label: string; icon: string }[] = [
    { id: 'live', label: 'Direct', icon: 'ph-broadcast' },
    { id: 'frames', label: 'Trames', icon: 'ph-list-dashes' },
    { id: 'request', label: 'Requête', icon: 'ph-upload-simple' },
  ];

  readonly streams = computed<StreamRecord[]>(() => {
    this.tracker.version();
    const q = this.search().trim().toLowerCase();
    const all = this.tracker.list();
    if (!q) return all;
    return all.filter(
      (s) => s.url.toLowerCase().includes(q) || (s.label ?? '').toLowerCase().includes(q)
    );
  });

  readonly selected = computed<StreamRecord | undefined>(() => {
    this.tracker.version();
    const pinned = this.pinnedId();
    if (!this.follow() && pinned) return this.tracker.byId(pinned);
    // En suivi : le flux vivant s'il y en a un, sinon le plus récent.
    const all = this.streams();
    return all.find((s) => s.state === 'streaming') ?? all[0];
  });

  readonly frames = computed<StreamFrame[]>(() => {
    this.tracker.version();
    return this.selected()?.frames ?? [];
  });

  /** Events porteurs de sens : tout sauf les tokens de texte. */
  readonly namedEvents = computed<StreamFrame[]>(() =>
    this.frames().filter(
      (f) => (f.kind === 'sse' && !!f.event) || f.kind === 'error' || f.kind === 'close' || f.kind === 'note'
    )
  );

  /** Lignes reçues hors grammaire SSE — signe que le backend n'envoie pas ce qu'on croit. */
  readonly rawChunkCount = computed(() => this.frames().filter((f) => f.kind === 'chunk').length);

  readonly counts = computed(() => {
    this.tracker.version();
    const all = this.tracker.list();
    return { live: all.filter((s) => s.state === 'streaming' || s.state === 'pending').length };
  });

  constructor() {
    effect(() => {
      const s = this.selected();
      if (!s || s.state !== 'streaming') return;
      // Dépendance explicite : suivre la longueur fait défiler à chaque token.
      s.text.length;
      queueMicrotask(() => {
        const el = this.pane() === 'frames' ? this.framesBox?.nativeElement : this.textBox?.nativeElement;
        if (el) el.scrollTop = el.scrollHeight;
      });
    });
  }

  ngOnInit(): void {
    // Filet de sécurité : l'installation réelle a lieu au démarrage
    // de l'application (app.config.ts), bien avant l'ouverture du panneau.
    this.tracker.install();
  }

  select(id: string): void {
    this.pinnedId.set(id);
    this.follow.set(false);
  }

  toggleExpand(seq: number): void {
    const next = new Set(this.expanded());
    if (next.has(seq)) next.delete(seq);
    else next.add(seq);
    this.expanded.set(next);
  }

  isExpanded(seq: number): boolean {
    return this.expanded().has(seq);
  }

  trackStream = (_: number, s: StreamRecord): string => s.id;
  trackFrame = (_: number, f: StreamFrame): number => f.seq;

  /** Nombre de trames de texte — la mesure la plus parlante d'un flux qui coule. */
  tokenCount(s: StreamRecord): number {
    return s.frames.filter((f) => f.kind === 'sse' && !f.event).length;
  }

  tagList(s: StreamRecord): { k: string; v: string }[] {
    if (!s.tags) return [];
    return Object.entries(s.tags)
      .filter(([, v]) => v !== undefined && v !== null && v !== '')
      .map(([k, v]) => ({ k, v: String(v).slice(0, 40) }));
  }

  dotColor(s: StreamRecord): string {
    switch (s.state) {
      case 'streaming':
        return '#22c55e';
      case 'done':
        return '#64748b';
      case 'aborted':
        return '#f59e0b';
      case 'error':
        return '#ef4444';
      default:
        return '#3b82f6';
    }
  }

  transportClass(t: StreamRecord['transport']): string {
    return t === 'eventsource'
      ? 'tw-bg-gov-secondary/20 tw-text-gov-secondary'
      : 'tw-bg-info-500/20 tw-text-info-500';
  }

  stateClass(state: StreamRecord['state']): string {
    switch (state) {
      case 'streaming':
        return 'tw-bg-success-500/20 tw-text-success-500';
      case 'done':
        return 'tw-bg-gray-500/20 tw-text-gray-300';
      case 'aborted':
        return 'tw-bg-warning-500/20 tw-text-warning-500';
      case 'error':
        return 'tw-bg-error-500/20 tw-text-error-500';
      default:
        return 'tw-bg-info-500/20 tw-text-info-500';
    }
  }

  stateLabel(state: StreamRecord['state']): string {
    switch (state) {
      case 'pending':
        return 'attente';
      case 'streaming':
        return 'en cours';
      case 'done':
        return 'terminé';
      case 'aborted':
        return 'annulé';
      default:
        return 'erreur';
    }
  }

  frameClass(f: StreamFrame): string {
    if (f.kind === 'error') return 'tw-bg-error-500/20 tw-text-error-500';
    if (f.kind === 'chunk') return 'tw-bg-warning-500/20 tw-text-warning-500';
    if (f.kind === 'request' || f.kind === 'open') return 'tw-bg-info-500/20 tw-text-info-500';
    if (f.kind === 'close') return 'tw-bg-gray-500/20 tw-text-gray-300';
    if (f.kind === 'note') return 'tw-bg-teal-primary/20 tw-text-teal-primary';
    return f.event
      ? 'tw-bg-gov-secondary/20 tw-text-gov-secondary'
      : 'tw-bg-success-500/20 tw-text-success-500';
  }

  frameLabel(f: StreamFrame): string {
    if (f.kind === 'sse') return f.event || 'token';
    if (f.kind === 'chunk') return 'brut';
    return f.event || f.kind;
  }

  /** Résumé d'un event : ce qu'on veut lire sans déplier. */
  summary(f: StreamFrame): string {
    const p = f.parsed;
    if (Array.isArray(p)) return `${p.length} élément(s)`;
    if (p && typeof p === 'object') {
      const keys = Object.keys(p as object);
      return `{ ${keys.slice(0, 4).join(', ')}${keys.length > 4 ? ', …' : ''} }`;
    }
    return (f.data ?? '').slice(0, 120);
  }

  detail(f: StreamFrame): string {
    return f.parsed !== undefined ? this.serialize(f.parsed) : (f.data ?? '(vide)');
  }

  framePreview(f: StreamFrame): string {
    if (f.data !== undefined && f.data !== '') {
      return f.data.length > 400 ? `${f.data.slice(0, 400)}…` : f.data;
    }
    if (f.parsed !== undefined) return this.serialize(f.parsed).slice(0, 400);
    return '';
  }

  serialize(value: unknown): string {
    try {
      return JSON.stringify(value, null, 2) ?? String(value);
    } catch {
      return String(value);
    }
  }

  formatBytes(n: number): string {
    if (n < 1024) return `${n} o`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} Ko`;
    return `${(n / 1024 / 1024).toFixed(1)} Mo`;
  }

  formatMs(ms: number): string {
    return ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`;
  }

  duration(s: StreamRecord): string {
    return this.formatMs((s.endedAt ?? Date.now()) - s.startedAt);
  }

  copyText(): void {
    const s = this.selected();
    if (!s) return;
    void navigator.clipboard?.writeText(s.text).then(() => {
      this.copied.set(true);
      setTimeout(() => this.copied.set(false), 1200);
    });
  }

  exportNdjson(): void {
    const s = this.selected();
    if (!s) return;
    const lines = s.frames.map((f) => JSON.stringify({ url: s.url, ...f })).join('\n');
    const blob = new Blob([lines], { type: 'application/x-ndjson' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `stream-${s.shortUrl.replace(/\W+/g, '-')}-${s.startedAt}.ndjson`;
    a.click();
    URL.revokeObjectURL(url);
  }
}
